import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import AttendancePage from './AttendancePage';
import MaterialsPage from './MaterialsPage';

const ENROLLMENT_STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  payment_failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
  completed: 'bg-blue-100 text-blue-700',
  pending_payment: 'bg-yellow-100 text-yellow-700'
};

const ENROLLMENT_STATUS_LABELS = {
  active: 'Active',
  payment_failed: 'Payment Failed',
  cancelled: 'Cancelled',
  completed: 'Completed',
  pending_payment: 'Pending Payment'
};

const StudentsPage = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam === 'attendance' ? 'attendance' : tabParam === 'materials' ? 'materials' : 'students');
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState({ totalActiveStudents: 0, totalEnrollments: 0, paymentIssues: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [courseFilter, setCourseFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Expanded student rows
  const [expandedStudents, setExpandedStudents] = useState({});

  // Promote modal
  const [promoteModal, setPromoteModal] = useState(null); // { enrollment, student }
  const [promoteTargetCourse, setPromoteTargetCourse] = useState('');
  const [promoteLoading, setPromoteLoading] = useState(false);
  const [promoteError, setPromoteError] = useState('');
  const [message, setMessage] = useState('');

  // Pending payment nudge — per-row in-flight + post-send confirm
  const [nudgingId, setNudgingId] = useState(null);
  const [nudgeConfirm, setNudgeConfirm] = useState(null); // { email, sentAt }

  const fetchStudents = async () => {
    try {
      const { data } = await api.get('/enroll/students');
      setStudents(data.students);
      setStats(data.stats);
      setCourses(data.courses);
    } catch (err) {
      console.error('Failed to load students:', err);
      setError('Failed to load students. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStudents(); }, []);

  // Filter students
  const filteredStudents = useMemo(() => {
    return students.filter(student => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const nameMatch = student.name?.toLowerCase().includes(query);
        const emailMatch = student.email?.toLowerCase().includes(query);
        if (!nameMatch && !emailMatch) return false;
      }
      if (courseFilter !== 'all') {
        if (!student.enrollments.some(e => e.course?._id === courseFilter)) return false;
      }
      if (statusFilter !== 'all') {
        if (!student.enrollments.some(e => e.status === statusFilter)) return false;
      }
      return true;
    });
  }, [students, searchQuery, courseFilter, statusFilter]);

  const toggleExpand = (studentId) => {
    setExpandedStudents(prev => ({ ...prev, [studentId]: !prev[studentId] }));
  };

  const getPrimaryStatus = (enrollments) => {
    if (enrollments.some(e => e.status === 'payment_failed')) return 'payment_failed';
    if (enrollments.some(e => e.status === 'active')) return 'active';
    if (enrollments.some(e => e.status === 'pending_payment')) return 'pending_payment';
    if (enrollments.some(e => e.status === 'completed')) return 'completed';
    return enrollments[0]?.status || 'cancelled';
  };

  const getTotalAttended = (enrollments) => {
    return enrollments.reduce((sum, e) => sum + (e.totalClassesAttended || 0), 0);
  };

  const getAttendancePercent = (enrollments) => {
    const attended = enrollments.reduce((sum, e) => sum + (e.totalClassesAttended || 0), 0);
    const missed = enrollments.reduce((sum, e) => sum + (e.totalClassesMissed || 0), 0);
    const total = attended + missed;
    return total > 0 ? Math.round((attended / total) * 100) : null;
  };

  const getCourseNames = (enrollments) => {
    const active = enrollments.filter(e => e.status === 'active' || e.status === 'pending_payment');
    return (active.length > 0 ? active : enrollments).map(e => e.course?.name).filter(Boolean);
  };

  const getEarliestEnrollment = (enrollments) => {
    const dates = enrollments.map(e => new Date(e.enrolledAt)).filter(d => !isNaN(d));
    return dates.length ? new Date(Math.min(...dates)) : null;
  };

  // ─── Pending Payment Nudge ────────────────────────────────────
  const handleNudge = async (enrollment, student) => {
    if (!enrollment?._id) return;
    setNudgingId(enrollment._id);
    try {
      const { data } = await api.post(`/enroll/${enrollment._id}/remind`);
      setNudgeConfirm({ email: data.sentTo || student?.email, sentAt: data.sentAt });
      // Refresh so the row reflects the new pendingNudgeSentAt stamp
      fetchStudents();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to send reminder');
      setTimeout(() => setMessage(''), 4000);
    } finally {
      setNudgingId(null);
    }
  };

  // ─── Promote flow ──────────────────────────────────────────────
  const openPromoteModal = (enrollment, student) => {
    setPromoteModal({ enrollment, student });
    setPromoteTargetCourse('');
    setPromoteError('');
  };

  const closePromoteModal = () => {
    setPromoteModal(null);
    setPromoteTargetCourse('');
    setPromoteError('');
  };

  // Get available courses for promotion (same studio, active, not the current course)
  const getPromotableCourses = (enrollment) => {
    return courses.filter(c =>
      c._id !== enrollment.course?._id &&
      c.status === 'active'
    );
  };

  const handlePromote = async () => {
    if (!promoteTargetCourse || !promoteModal) return;
    setPromoteLoading(true);
    setPromoteError('');
    try {
      const { data } = await api.post(`/enroll/${promoteModal.enrollment._id}/promote`, {
        newCourseId: promoteTargetCourse
      });
      const targetCourse = courses.find(c => c._id === promoteTargetCourse);
      setMessage(`${promoteModal.student.name || 'Student'} promoted to ${targetCourse?.name || data.enrollment.courseName}${data.payment ? ` (enrollment fee diff: $${data.payment.totalCharged})` : ''}`);
      setTimeout(() => setMessage(''), 5000);
      closePromoteModal();
      // Refresh student list
      setLoading(true);
      fetchStudents();
    } catch (err) {
      setPromoteError(err.response?.data?.error || 'Failed to promote student');
    } finally {
      setPromoteLoading(false);
    }
  };

  // Get the selected target course details for the modal preview
  const targetCourseDetails = promoteTargetCourse
    ? courses.find(c => c._id === promoteTargetCourse)
    : null;

  const currentCourseMonthly = promoteModal?.enrollment?.course?.billingPlans?.find(p => p.frequency === 'monthly');
  const targetCourseMonthly = targetCourseDetails?.billingPlans?.find(p => p.frequency === 'monthly');

  // Enrollment fee = monthly amount (advance deposit)
  const enrollmentFeeDiff = (currentCourseMonthly && targetCourseMonthly)
    ? targetCourseMonthly.amount - currentCourseMonthly.amount
    : null;

  // ─── Render ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading students...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-6 rounded-xl border border-red-100 text-center">
        <p className="text-red-600">{error}</p>
        <button onClick={() => window.location.reload()} className="mt-3 text-sm text-brand-500 hover:text-brand-600 font-medium">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Students</h1>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('students')}
          className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'students' ? 'text-brand-500 border-brand-500' : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          Students
        </button>
        <button
          onClick={() => setActiveTab('attendance')}
          className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'attendance' ? 'text-brand-500 border-brand-500' : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          Attendance
        </button>
        <button
          onClick={() => setActiveTab('materials')}
          className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'materials' ? 'text-brand-500 border-brand-500' : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          Materials
        </button>
      </div>

      {activeTab === 'attendance' ? (
        <AttendancePage />
      ) : activeTab === 'materials' ? (
        <MaterialsPage />
      ) : (
      <>

      {message && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{message}</div>
      )}

      {/* Summary Stats — click any tile to filter the list below */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <button
          type="button"
          onClick={() => {
            setStatusFilter('active');
            setCourseFilter('all');
            setSearchQuery('');
            document.getElementById('students-list-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          className={`bg-white p-5 rounded-xl border shadow-sm text-left hover:bg-brand-50/40 transition-colors ${
            statusFilter === 'active' ? 'border-brand-400 ring-2 ring-brand-100' : 'border-gray-100'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.totalActiveStudents}</div>
              <div className="text-sm text-gray-500 mt-1">Active Students</div>
            </div>
            <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center">
              <svg className="w-5 h-5 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => {
            setStatusFilter('all');
            setCourseFilter('all');
            setSearchQuery('');
            document.getElementById('students-list-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          className={`bg-white p-5 rounded-xl border shadow-sm text-left hover:bg-green-50/40 transition-colors ${
            statusFilter === 'all' && courseFilter === 'all' && !searchQuery ? 'border-green-400 ring-2 ring-green-100' : 'border-gray-100'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.totalEnrollments}</div>
              <div className="text-sm text-gray-500 mt-1">Total Enrollments</div>
            </div>
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
              <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => {
            setStatusFilter('payment_failed');
            setCourseFilter('all');
            setSearchQuery('');
            document.getElementById('students-list-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          className={`bg-white p-5 rounded-xl border shadow-sm text-left transition-colors ${
            statusFilter === 'payment_failed' ? 'border-red-400 ring-2 ring-red-100' : 'border-gray-100'
          } ${stats.paymentIssues > 0 ? 'hover:bg-red-50/40' : 'opacity-90'}`}
          disabled={stats.paymentIssues === 0}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-2xl font-bold ${stats.paymentIssues > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {stats.paymentIssues}
              </div>
              <div className="text-sm text-gray-500 mt-1">Payment Issues</div>
            </div>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stats.paymentIssues > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
              <svg className={`w-5 h-5 ${stats.paymentIssues > 0 ? 'text-red-500' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
          </div>
        </button>
      </div>

      {/* Search & Filters */}
      <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm mb-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="relative flex-1 w-full sm:w-auto">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by name or email"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
            />
          </div>
          <select value={courseFilter} onChange={e => setCourseFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent">
            <option value="all">All Courses</option>
            {courses.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent">
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="payment_failed">Payment Failed</option>
            <option value="pending_payment">Pending Payment</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Student List */}
      <div id="students-list-anchor" />
      {students.length === 0 ? (
        <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm text-center text-gray-500">
          No enrolled students yet. Students will appear here after they enroll in your courses.
        </div>
      ) : filteredStudents.length === 0 ? (
        <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm text-center text-gray-500">
          No students match your search or filters.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="hidden md:grid md:grid-cols-12 bg-gray-50 border-b border-gray-100 px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="col-span-3">Student</div>
            <div className="col-span-2">Courses</div>
            <div className="col-span-2 text-center">Status</div>
            <div className="col-span-1 text-center">Classes</div>
            <div className="col-span-2 text-center">Attendance</div>
            <div className="col-span-2 text-right">Enrolled</div>
          </div>

          <div className="divide-y divide-gray-100">
            {filteredStudents.map(student => {
              const isExpanded = expandedStudents[student._id];
              const primaryStatus = getPrimaryStatus(student.enrollments);
              const totalAttended = getTotalAttended(student.enrollments);
              const attendancePct = getAttendancePercent(student.enrollments);
              const courseNames = getCourseNames(student.enrollments);
              const earliestDate = getEarliestEnrollment(student.enrollments);

              return (
                <div key={student._id}>
                  {/* Main row */}
                  <div className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => toggleExpand(student._id)}>
                    {/* Desktop: grid layout */}
                    <div className="hidden md:grid md:grid-cols-12 md:items-center">
                      <div className="col-span-3 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-semibold text-brand-600">
                            {(student.name || '?')[0].toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{student.name || 'Unknown'}</div>
                          <div className="text-xs text-gray-500 truncate">{student.email || ''}</div>
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="flex flex-wrap gap-1">
                          {courseNames.length <= 2 ? (
                            courseNames.map((name, i) => (
                              <span key={i} className="inline-block px-2 py-0.5 bg-brand-50 text-brand-700 text-xs rounded-full truncate max-w-[140px]">{name}</span>
                            ))
                          ) : (
                            <>
                              <span className="inline-block px-2 py-0.5 bg-brand-50 text-brand-700 text-xs rounded-full truncate max-w-[140px]">{courseNames[0]}</span>
                              <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">+{courseNames.length - 1} more</span>
                            </>
                          )}
                          {courseNames.length === 0 && <span className="text-xs text-gray-400">No courses</span>}
                        </div>
                      </div>
                      <div className="col-span-2 text-center">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${ENROLLMENT_STATUS_COLORS[primaryStatus] || 'bg-gray-100 text-gray-500'}`}>
                          {ENROLLMENT_STATUS_LABELS[primaryStatus] || primaryStatus}
                        </span>
                      </div>
                      <div className="col-span-1 text-center">
                        <span className="text-sm text-gray-700">{totalAttended}</span>
                      </div>
                      <div className="col-span-2 text-center">
                        {attendancePct !== null ? (
                          <span className={`text-sm font-medium ${
                            attendancePct >= 80 ? 'text-green-600' : attendancePct >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {attendancePct}%
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">--</span>
                        )}
                      </div>
                      <div className="col-span-2 flex items-center justify-end gap-2">
                        <span className="text-sm text-gray-500">{earliestDate ? earliestDate.toLocaleDateString() : '--'}</span>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>

                    {/* Mobile: card layout */}
                    <div className="md:hidden">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-semibold text-brand-600">
                              {(student.name || '?')[0].toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{student.name || 'Unknown'}</div>
                            <div className="text-xs text-gray-500 truncate">{student.email || ''}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ENROLLMENT_STATUS_COLORS[primaryStatus] || 'bg-gray-100 text-gray-500'}`}>
                            {ENROLLMENT_STATUS_LABELS[primaryStatus] || primaryStatus}
                          </span>
                          <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                      <div className="mt-2 ml-12 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                        {courseNames.slice(0, 1).map((name, i) => (
                          <span key={i} className="px-2 py-0.5 bg-brand-50 text-brand-700 rounded-full truncate max-w-[160px]">{name}</span>
                        ))}
                        {courseNames.length > 1 && <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">+{courseNames.length - 1}</span>}
                        <span>{totalAttended} classes</span>
                        {attendancePct !== null && (
                          <span className={`font-medium ${attendancePct >= 80 ? 'text-green-600' : attendancePct >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {attendancePct}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-4 pb-4 bg-gray-50 border-t border-gray-100">
                      <div className="pt-3">
                        {student.phone && (
                          <div className="mb-3 text-xs text-gray-500">Phone: {student.phone}</div>
                        )}

                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                          Enrollment Details ({student.enrollments.length})
                        </div>

                        <div className="space-y-2">
                          {student.enrollments.map(enrollment => (
                            <div key={enrollment._id} className="bg-white rounded-lg border border-gray-100 p-3">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-gray-900">
                                      {enrollment.course?.name || 'Unknown Course'}
                                    </span>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ENROLLMENT_STATUS_COLORS[enrollment.status] || 'bg-gray-100 text-gray-500'}`}>
                                      {ENROLLMENT_STATUS_LABELS[enrollment.status] || enrollment.status}
                                    </span>
                                    {enrollment.cancelReason === 'promoted' && (
                                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-brand-50 text-brand-600">Promoted</span>
                                    )}
                                  </div>
                                  {enrollment.course && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      {enrollment.course.danceStyle && <span>{enrollment.course.danceStyle}</span>}
                                      {enrollment.course.danceStyle && enrollment.course.levelName && <span> &middot; </span>}
                                      {enrollment.course.levelName && <span>{enrollment.course.levelName}</span>}
                                      {(enrollment.course.danceStyle || enrollment.course.levelName) && enrollment.course.type && <span> &middot; </span>}
                                      {enrollment.course.type && <span className="capitalize">{enrollment.course.type.replace('_', ' ')}</span>}
                                    </div>
                                  )}
                                  {enrollment.billingPlan && (
                                    <div className="text-xs text-gray-400 mt-0.5">
                                      {enrollment.billingPlan.name}
                                      {enrollment.billingPlan.amount > 0 && (
                                        <span> &middot; ${enrollment.billingPlan.amount}/{enrollment.billingPlan.frequency?.replace('_', ' ')}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 text-xs text-gray-500 flex-shrink-0">
                                  <div className="text-center">
                                    <div className="text-sm font-semibold text-gray-700">{enrollment.totalClassesAttended}</div>
                                    <div>Attended</div>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-sm font-semibold text-gray-700">{enrollment.totalClassesMissed}</div>
                                    <div>Missed</div>
                                  </div>
                                  {enrollment.consecutiveAbsences > 0 && (
                                    <div className="text-center">
                                      <div className="text-sm font-semibold text-red-600">{enrollment.consecutiveAbsences}</div>
                                      <div>Streak</div>
                                    </div>
                                  )}
                                  <div className="text-center">
                                    <div className="text-sm text-gray-500">{new Date(enrollment.enrolledAt).toLocaleDateString()}</div>
                                    <div>Enrolled</div>
                                  </div>
                                </div>
                              </div>

                              {/* Promote button — only for active enrollments */}
                              {enrollment.status === 'active' && (
                                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                                  <div className="text-xs text-gray-400">
                                    {enrollment.totalClassesAttended} classes completed
                                  </div>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openPromoteModal(enrollment, student); }}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-600 bg-brand-50 rounded-lg hover:bg-brand-100 transition-colors"
                                  >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5 12 3m0 0 7.5 7.5M12 3v18" />
                                    </svg>
                                    Promote / Move
                                  </button>
                                </div>
                              )}

                              {/* Send Reminder — pending_payment rows. Recovers abandoned-cart conversions. */}
                              {enrollment.status === 'pending_payment' && (
                                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between gap-3">
                                  <div className="text-xs text-gray-400 min-w-0 truncate">
                                    {enrollment.pendingNudgeSentAt
                                      ? `Last reminded ${new Date(enrollment.pendingNudgeSentAt).toLocaleDateString()}`
                                      : 'Started but didn\'t finish payment'}
                                  </div>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleNudge(enrollment, student); }}
                                    disabled={nudgingId === enrollment._id}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50 flex-shrink-0"
                                  >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    {nudgingId === enrollment._id ? 'Sending…' : 'Send Reminder'}
                                  </button>
                                </div>
                              )}

                              {enrollment.cancelledAt && (
                                <div className="mt-2 text-xs text-red-500">
                                  Cancelled on {new Date(enrollment.cancelledAt).toLocaleDateString()}
                                </div>
                              )}
                              {enrollment.completedAt && (
                                <div className="mt-2 text-xs text-blue-500">
                                  {enrollment.cancelReason === 'promoted' ? 'Promoted' : 'Completed'} on {new Date(enrollment.completedAt).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-500">
            Showing {filteredStudents.length} of {students.length} student{students.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* ─── Promote Modal ─────────────────────────────────────────── */}
      {promoteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={closePromoteModal}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Promote Student</h2>
            <p className="text-sm text-gray-500 mb-5">
              Move <span className="font-medium text-gray-700">{promoteModal.student.name || promoteModal.student.email}</span> from{' '}
              <span className="font-medium text-gray-700">{promoteModal.enrollment.course?.name}</span> to a new course.
            </p>

            {/* Current enrollment info */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-700">Current: {promoteModal.enrollment.course?.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {promoteModal.enrollment.course?.danceStyle}
                    {promoteModal.enrollment.course?.levelName && ` \u00b7 ${promoteModal.enrollment.course.levelName}`}
                    {' \u00b7 '}{promoteModal.enrollment.totalClassesAttended} classes attended
                  </div>
                </div>
                {currentCourseMonthly && (
                  <div className="text-right">
                    <div className="font-medium text-gray-700">${currentCourseMonthly.amount}/mo</div>
                  </div>
                )}
              </div>
            </div>

            {/* Target course selector */}
            <label className="block text-sm font-medium text-gray-700 mb-1">Move to</label>
            <select
              value={promoteTargetCourse}
              onChange={e => setPromoteTargetCourse(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent mb-4"
            >
              <option value="">Select a course...</option>
              {getPromotableCourses(promoteModal.enrollment).map(c => {
                const monthly = c.billingPlans?.find(p => p.frequency === 'monthly');
                return (
                  <option key={c._id} value={c._id}>
                    {c.name}
                    {c.danceStyle ? ` (${c.danceStyle}` : ''}
                    {c.levelName ? ` \u00b7 ${c.levelName}` : ''}
                    {c.danceStyle ? ')' : ''}
                    {monthly ? ` — $${monthly.amount}/mo` : ''}
                  </option>
                );
              })}
            </select>

            {/* Price comparison preview */}
            {targetCourseDetails && (
              <div className="bg-brand-50 rounded-lg p-3 mb-4 text-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-brand-700">New: {targetCourseDetails.name}</div>
                    <div className="text-xs text-brand-500 mt-0.5">
                      {targetCourseDetails.danceStyle}
                      {targetCourseDetails.levelName && ` \u00b7 ${targetCourseDetails.levelName}`}
                    </div>
                  </div>
                  {targetCourseMonthly && (
                    <div className="text-right">
                      <div className="font-medium text-brand-700">${targetCourseMonthly.amount}/mo</div>
                    </div>
                  )}
                </div>

                {enrollmentFeeDiff !== null && enrollmentFeeDiff !== 0 && (
                  <div className="mt-3 pt-3 border-t border-brand-200">
                    {enrollmentFeeDiff > 0 ? (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-brand-600">Enrollment fee difference</span>
                        <span className="font-semibold text-brand-700">${enrollmentFeeDiff} charged now</span>
                      </div>
                    ) : (
                      <div className="text-brand-600 text-sm">
                        Downgrade — no enrollment fee charge
                      </div>
                    )}
                  </div>
                )}
                {enrollmentFeeDiff === 0 && (
                  <div className="mt-3 pt-3 border-t border-brand-200 text-sm text-brand-600">
                    Same enrollment fee — no additional charge
                  </div>
                )}
              </div>
            )}

            <div className="text-xs text-gray-400 mb-4">
              The current subscription will run until the end of its billing cycle, then the new course subscription begins automatically at the new rate.
            </div>

            {promoteError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{promoteError}</div>
            )}

            <div className="flex justify-end gap-3">
              <button onClick={closePromoteModal} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">
                Cancel
              </button>
              <button
                onClick={handlePromote}
                disabled={!promoteTargetCourse || promoteLoading}
                className="bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors"
              >
                {promoteLoading ? 'Promoting...' : 'Confirm Promotion'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending-payment nudge confirmation */}
      {nudgeConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Reminder sent</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              We emailed <strong>{nudgeConfirm.email}</strong> a link to finish their enrollment.
              They'll land back on the same checkout in one click.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setNudgeConfirm(null)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-brand-500 text-white hover:bg-brand-600"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      </>
      )}
    </div>
  );
};

export default StudentsPage;
